import React from 'react';

const SimpleCharts = () => {
  // Test data
  const barData = [
    { label: 'Jan', value: 12, max: 20 },
    { label: 'Feb', value: 19, max: 20 },
    { label: 'Mar', value: 3, max: 20 },
    { label: 'Apr', value: 5, max: 20 },
    { label: 'May', value: 2, max: 20 },
  ];

  const groupedData = [
    { label: 'Q1', values: [{ name: 'A', value: 12, color: '#3b82f6' }, { name: 'B', value: 10, color: '#10b981' }, { name: 'C', value: 8, color: '#8b5cf6' }] },
    { label: 'Q2', values: [{ name: 'A', value: 19, color: '#3b82f6' }, { name: 'B', value: 15, color: '#10b981' }, { name: 'C', value: 11, color: '#8b5cf6' }] },
    { label: 'Q3', values: [{ name: 'A', value: 8, color: '#3b82f6' }, { name: 'B', value: 12, color: '#10b981' }, { name: 'C', value: 14, color: '#8b5cf6' }] },
    { label: 'Q4', values: [{ name: 'A', value: 15, color: '#3b82f6' }, { name: 'B', value: 18, color: '#10b981' }, { name: 'C', value: 10, color: '#8b5cf6' }] },
  ];

  const pieData = [
    { label: 'Red', value: 300, percentage: 66.7, color: '#ef4444' },
    { label: 'Blue', value: 50, percentage: 11.1, color: '#3b82f6' },
    { label: 'Yellow', value: 100, percentage: 22.2, color: '#eab308' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          HTML/CSS Charts Test Page
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-2">
          Pure HTML/CSS visualizations - NO external libraries - GUARANTEED to work
        </p>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            ⚠️ This is a fallback solution using only HTML/CSS. If you see colored bars below, this approach works!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Simple Bar Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Test 1: Simple Bar Chart (HTML/CSS)
            </h2>
            <div className="space-y-4">
              {barData.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.value}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-8">
                    <div
                      className="bg-blue-600 h-8 rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                      style={{ width: `${(item.value / item.max) * 100}%` }}
                    >
                      <span className="text-white text-xs font-bold">{item.value}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              ✓ Should show: Blue bars with values
            </p>
          </div>

          {/* Grouped Bar Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Test 2: Grouped Bar Chart (HTML/CSS)
            </h2>
            <div className="space-y-4">
              {groupedData.map((group, groupIndex) => (
                <div key={groupIndex}>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{group.label}</div>
                  <div className="flex gap-2">
                    {group.values.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex-1">
                        <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded flex flex-col justify-end">
                          <div
                            className="rounded flex items-center justify-center text-white text-xs font-bold transition-all duration-500"
                            style={{ 
                              height: `${(item.value / 20) * 100}%`,
                              backgroundColor: item.color
                            }}
                          >
                            {item.value}
                          </div>
                        </div>
                        <div className="text-xs text-center mt-1 text-gray-600 dark:text-gray-400">{item.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              ✓ Should show: 3 colored bars (A, B, C) per quarter
            </p>
          </div>

          {/* Pie Chart (as list with bars) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Test 3: Pie Chart Alternative (HTML/CSS)
            </h2>
            <div className="space-y-3">
              {pieData.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.value} ({item.percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-6">
                    <div
                      className="h-6 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${item.percentage}%`,
                        backgroundColor: item.color
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              ✓ Should show: Colored horizontal bars with percentages
            </p>
          </div>

          {/* Line Chart Alternative */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Test 4: Line Chart Alternative (HTML/CSS)
            </h2>
            <div className="relative h-48 border-l-2 border-b-2 border-gray-300 dark:border-gray-600 p-4">
              <div className="absolute inset-0 flex items-end justify-around px-4 pb-8">
                {[33, 53, 85, 41, 44].map((value, index) => (
                  <div key={index} className="flex flex-col items-center flex-1">
                    <div
                      className="w-2 bg-green-600 rounded-t transition-all duration-500"
                      style={{ height: `${(value / 100) * 100}%` }}
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][index]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              ✓ Should show: Green vertical bars for each day
            </p>
          </div>

        </div>

        {/* Result */}
        <div className="mt-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            ✅ If You Can See Colored Bars Above
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            This proves that HTML/CSS visualizations work perfectly in your environment.
            I can use this approach for all your dashboards - it will be:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li>✅ Guaranteed to work (no library dependencies)</li>
            <li>✅ Fast and responsive</li>
            <li>✅ Clean and professional appearance</li>
            <li>✅ Fully customizable</li>
            <li>✅ No external dependencies = no compatibility issues</li>
          </ul>
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Please confirm if you see the colored bars above, and I'll implement this approach throughout your dashboards.
          </p>
        </div>

      </div>
    </div>
  );
};

export default SimpleCharts;
